import { autoFormatEnergyValue } from "@solarschools/component-lib/src/utilities/EnergyUnits";
import { autoFormatMassValue } from "../../../utilities/UnitsAndKeys/MassUnits";
import { CarouselText } from "./EnergyCarousel.styles";

export const carouselSettings = {
  autoplay: true,
  speed: 1000,
  autoplaySpeed: 8000,
  pauseOnHover: true,
  arrows: false,
};

export const makeSlides = (
  totalCo2Saved,
  totalEnergyGenerated,
  totalGridConsumption,
  totalEmissions
) => {
  const slides = [];
  if (totalCo2Saved > 0) {
    slides.push({
      info: (
        <CarouselText variant="body2">
          A standard soccer ball a volume of 0.005575m<sup>3</sup>. At standard pressure (1
          atm) and 15 degrees Celsius a metric tone of CO<sub>2</sub>-e would fill
          a sphere approximately 10m (523.6m<sup>3</sup>) across (density of CO<sub>2</sub>-e
          = 1.876kg/m<sup>3</sup>).
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Avoided {autoFormatMassValue(totalCo2Saved)} of CO<sub>2</sub>
          -e emissions, enough to fill{" "}
          {(
            ((totalCo2Saved / 1000) * (523.6 / 0.005575)) /
            1000000
          ).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}{" "}
          million soccer balls.
        </CarouselText>
      ),
      img: "ball",
    });
  }
  if (totalEmissions > 1000000) {
    slides.push({
      info: (
        <CarouselText variant="body2">
          An average car emits 2 tonnes of carbon dioxide per year.
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          CO<sub>2</sub>-e Emissions of {autoFormatMassValue(totalEmissions)} is
          equivalent to {Math.ceil(totalEmissions / 1000 / 1000 / 2).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} cars driving
          for 1 year.
        </CarouselText>
      ),
      img: "car",
    });
  }
  if (totalCo2Saved > 0) {
    slides.push({
      info: (
        <CarouselText variant="body2">
          A tree can sequester up to 21.77kg of CO<sub>2</sub>-e per year.
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Avoided {autoFormatMassValue(totalCo2Saved)}s of CO<sub>2</sub>-e emissions, equivalent to{" "}
          {Math.ceil(totalCo2Saved / 21.77 / 1000 / 10).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}{" "}
          trees growing for 10 years.
        </CarouselText>
      ),
      img: "tree",
    });
    slides.push({
      info: (
        <CarouselText variant="body2">
          An average hot air balloon envelope has a volume of 2,800 m<sup>3</sup> (5.3476t of
          CO<sub>2</sub>-e).  At standard pressure (1 atm) and 15 degrees Celsius
          a metric tone of CO<sub>2</sub>-e would fill a sphere approximately 10m
          across (density of CO<sub>2</sub>-e = 1.876kg/m<sup>3</sup>).
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Avoided {autoFormatMassValue(totalCo2Saved)}s of CO<sub>2</sub>-e emissions, enough to fill{" "}
          {(totalCo2Saved / 1000 / 1000 / 5.3476).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })} hot air balloons.
        </CarouselText>
      ),
      img: "hotairballoons",
    });
    slides.push({
      info: (
        <CarouselText variant="body2">
          The average mass of a grown African forest elephant is 2,700kg.
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Avoided {autoFormatMassValue(totalCo2Saved)}s of CO<sub>2</sub>-e being released into the atmosphere, equivalent in mass to{" "}
          {(totalCo2Saved / 2700 / 1000).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          })} African forest elephants.
        </CarouselText>
      ),
      img: "elephants",
    });
  }
  if (totalEnergyGenerated > 0) {
    slides.push({
      info: (
        <CarouselText variant="body2">
          A tour De France cyclist burns on average 6,071 nutritional calories.
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Lifetime Generation of {autoFormatEnergyValue(totalEnergyGenerated)} is
          equivalent to {Math.ceil(totalEnergyGenerated / 7.05585 / 1000).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} cyclists completing the Tour De France, a combined total of{" "}
          {((totalEnergyGenerated / 7.05585 / 1000) * 3500).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
          km.
        </CarouselText>
      ),
      img: "bike",
    });
    slides.push({
      info: (
        <CarouselText variant="body2">
          1kg of TNT stores 1.2 kilowatt hours of energy that is released
          explosively.
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Lifetime Generation of {autoFormatEnergyValue(totalEnergyGenerated)} is
          equivalent to the energy stored in{" "}
          {(totalEnergyGenerated / 1.2 / 1000).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
          kg of TNT.
        </CarouselText>
      ),
      img: "tnt",
    });
  }
  slides.push({
    info: (
      <CarouselText variant="body2">
        1kg of coal stores 8.141kwh of energy.
      </CarouselText>
    ),
    text: (
      <CarouselText variant="body2">
        Grid Consumption of {autoFormatEnergyValue(totalGridConsumption)} is
        equal to the heat energy from burning{" "}
        {(totalGridConsumption / 8.141 / 1000 / 1000).toLocaleString("en-AU", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}{" "}
        tonnes of coal.
      </CarouselText>
    ),
    img: "coal",
  });
  if (totalEnergyGenerated > 0) {
    slides.push({
      info: (
        <CarouselText variant="body2">
          A high speed train uses about 25kWh to travel 1km at 330kmh.
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Lifetime Generation {autoFormatEnergyValue(totalEnergyGenerated)} is
          equivalent to travelling{" "}
          {(totalEnergyGenerated / 25 / 1000).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
          km in a high speed train at 330km an hour.
        </CarouselText>
      ),
      img: "train",
    });
    slides.push({
      info: (
        <CarouselText variant="body2">
          An average frost free fridge uses 4.51kWh of energy a day.
        </CarouselText>
      ),
      text: (
        <CarouselText variant="body2">
          Lifetime Generation {autoFormatEnergyValue(totalEnergyGenerated)} is
          enough to run {(totalEnergyGenerated / 4.51 / 1000).toLocaleString("en-AU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          })} frost free
          fridges for one year.
        </CarouselText>
      ),
      img: "fridge",
    });
  }

  return slides;
};


// WEBPACK FOOTER //
// ./resources/assets/js/app/SchoolProfileView/components/EnergyCarousel/EnergyCarousel.utils.js